import { useEffect, useRef } from "react";
import { useI18n } from "@/lib/useI18n";
import { translateTexts } from "@/lib/translate.functions";

// Elements whose text we should NOT touch
const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "SVG", "PATH",
  "INPUT", "TEXTAREA", "SELECT", "OPTION",
]);

function shouldSkipNode(node: Node): boolean {
  let el: Node | null = node.parentNode;
  while (el && el.nodeType === 1) {
    const e = el as HTMLElement;
    if (SKIP_TAGS.has(e.tagName)) return true;
    if (e.hasAttribute("data-no-translate")) return true;
    if (e.getAttribute("translate") === "no") return true;
    el = e.parentNode;
  }
  return false;
}

function collectTextNodes(root: HTMLElement): Text[] {
  const out: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const t = node.nodeValue ?? "";
      const trimmed = t.trim();
      if (!trimmed) return NodeFilter.FILTER_REJECT;
      if (trimmed.length < 2) return NodeFilter.FILTER_REJECT;
      // Skip pure numeric / symbol strings
      if (/^[\s\d\W_]+$/.test(trimmed)) return NodeFilter.FILTER_REJECT;
      if (shouldSkipNode(node)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n: Node | null;
  while ((n = walker.nextNode())) out.push(n as Text);
  return out;
}

type Cache = Record<string, string>;

function loadCache(lang: string): Cache {
  try {
    return JSON.parse(localStorage.getItem(`brandin_tr_${lang}`) || "{}");
  } catch {
    return {};
  }
}
function saveCache(lang: string, cache: Cache) {
  try {
    localStorage.setItem(`brandin_tr_${lang}`, JSON.stringify(cache));
  } catch {
    /* ignore quota */
  }
}

const ORIG = new WeakMap<Text, string>();

async function translateBatch(lang: string, texts: string[]): Promise<string[]> {
  try {
    const res = await translateTexts({ data: { lang, texts } });
    return res.translations;
  } catch (e) {
    console.warn("[auto-translate] failed:", e);
    return texts;
  }
}

/**
 * Auto-translates every visible text node inside its parent page container
 * whenever the active language changes. Uses Lovable AI + localStorage cache.
 * Add `data-no-translate` on any element you want to leave untouched.
 */
export function AutoTranslate() {
  const { lang } = useI18n();
  const runIdRef = useRef(0);

  useEffect(() => {
    const runId = ++runIdRef.current;
    const root = document.body;
    if (!root) return;

    // Restore originals first
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let n: Node | null;
    while ((n = walker.nextNode())) {
      const orig = ORIG.get(n as Text);
      if (orig != null) (n as Text).nodeValue = orig;
    }

    if (lang === "en") return;

    let cancelled = false;
    const cache = loadCache(lang);

    const process = async () => {
      // Small delay so React finishes rendering current frame
      await new Promise((r) => setTimeout(r, 50));
      if (cancelled || runIdRef.current !== runId) return;

      const nodes = collectTextNodes(root);
      const needFetch: string[] = [];
      const seen = new Set<string>();
      for (const node of nodes) {
        const original = ORIG.get(node) ?? node.nodeValue ?? "";
        if (!ORIG.has(node)) ORIG.set(node, original);
        const key = original.trim();
        if (cache[key]) {
          // apply cached translation preserving surrounding whitespace
          node.nodeValue = original.replace(key, cache[key]);
          continue;
        }
        if (!seen.has(key)) {
          seen.add(key);
          needFetch.push(key);
        }
      }

      // Batch remaining texts (chunks of 40 to keep prompts small)
      const CHUNK = 40;
      for (let i = 0; i < needFetch.length; i += CHUNK) {
        if (cancelled || runIdRef.current !== runId) return;
        const chunk = needFetch.slice(i, i + CHUNK);
        const translated = await translateBatch(lang, chunk);
        chunk.forEach((k, idx) => {
          cache[k] = translated[idx] ?? k;
        });
        // Apply translations to any matching nodes currently on page
        const currentNodes = collectTextNodes(root);
        for (const node of currentNodes) {
          const original = ORIG.get(node) ?? node.nodeValue ?? "";
          if (!ORIG.has(node)) ORIG.set(node, original);
          const key = original.trim();
          if (cache[key]) node.nodeValue = original.replace(key, cache[key]);
        }
        saveCache(lang, cache);
      }
    };

    void process();

    // Observe subsequent DOM changes (carousels, dialogs) and translate new text
    const observer = new MutationObserver((mutations) => {
      const cacheNow = loadCache(lang);
      const pending: string[] = [];
      const seen = new Set<string>();

      for (const m of mutations) {
        m.addedNodes.forEach((added) => {
          if (added.nodeType !== 1 && added.nodeType !== 3) return;
          const container: HTMLElement =
            added.nodeType === 1
              ? (added as HTMLElement)
              : (added.parentElement as HTMLElement);
          if (!container) return;
          const nodes = added.nodeType === 3 ? [added as Text] : collectTextNodes(container);
          for (const node of nodes) {
            const original = ORIG.get(node) ?? node.nodeValue ?? "";
            if (!ORIG.has(node)) ORIG.set(node, original);
            const key = original.trim();
            if (!key) continue;
            if (cacheNow[key]) {
              node.nodeValue = original.replace(key, cacheNow[key]);
            } else if (!seen.has(key)) {
              seen.add(key);
              pending.push(key);
            }
          }
        });
      }

      if (pending.length) {
        (async () => {
          const CHUNK = 40;
          for (let i = 0; i < pending.length; i += CHUNK) {
            if (runIdRef.current !== runId) return;
            const chunk = pending.slice(i, i + CHUNK);
            const translated = await translateBatch(lang, chunk);
            chunk.forEach((k, idx) => (cacheNow[k] = translated[idx] ?? k));
            const currentNodes = collectTextNodes(root);
            for (const node of currentNodes) {
              const original = ORIG.get(node) ?? node.nodeValue ?? "";
              if (!ORIG.has(node)) ORIG.set(node, original);
              const key = original.trim();
              if (cacheNow[key]) node.nodeValue = original.replace(key, cacheNow[key]);
            }
            saveCache(lang, cacheNow);
          }
        })();
      }
    });
    observer.observe(root, { subtree: true, childList: true, characterData: false });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [lang]);

  return null;
}
